import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import {
  type AgentChatArtifact,
  type AgentChatCanvasMutation,
  type AgentChatDecision,
  type AgentChatDecisionResponse,
  parseDecision,
  streamAgentChat,
} from '@/services/agents/chat-stream';
import {
  addedNodeDisplayName,
  buildMutationLabel,
} from '@/components/chat/helpers/mutation-label';
import type { WorkflowMutationEntry } from '@/components/chat/types';
import {
  type AgentChatMessage,
  type ConversationRole,
  getAgentConversationDetail,
  recordContextSwitch,
} from '@/services/agents/config';
import {
  formatAgentMessageWithAttachments,
  type UploadedAgentFile,
} from '@/services/agents/files';
import {
  extractRunIdFromText,
  isRunInitiatingTool,
} from '@/services/agents/run-events-stream';
import { isApplyChangesIntent } from '@/components/chat/helpers/apply-changes-intent';
import { useNotification } from '@/components/notifications';
import { getStructDisplayLabel } from '@/stores/helpers/node-naming';
import { useChatApplyStore } from '@/stores/chat-apply-store';
import { useNodeStore } from '@/stores/node-store';
import { useWorkflowStore } from '@/stores/workflow-store';

import { conversationWriteAccess } from './conversation-access';
import {
  type ContextSwitch,
  UNSAVED_CANVAS_IDENTITY,
  contextFromSurface,
  contextIdentity,
  contextSwitchFromMetadata,
  recordsOnSend,
} from './context-switch';
import { wrapWithCustomInstructions } from './custom-instructions-popover';
import {
  pendingClarificationIn,
  pendingReviewIn,
  type RelayedRunEvent,
  runEventFromMetadata,
} from './run-event';
import {
  wrapWithSkillDirective,
  type ArmedSkill,
} from './helpers/skill-directive';
import {
  type TurnSegment,
  appendActivity,
  appendThinking,
  closeThinking,
  finishTimeline,
  endsProse,
  hasProse,
  isThinkingMarker,
  sealText,
} from './turn-timeline';
import type { SurfaceContext } from './types';

export interface AgentChatUiMessage extends AgentChatMessage {
  _localId: string;
  _pending?: boolean;
  _stalled?: boolean;
  /** True while SSE text deltas are still arriving for this bubble. */
  _streaming?: boolean;
  /** File references produced during the turn (e.g. generated docs). */
  _artifacts?: AgentChatArtifact[];
  /** Workflow runs started during this turn, oldest first. */
  _runIds?: string[];
  /**
   * True when `_runIds` was seeded from conversation history rather than
   * grown live via `attachRun` during this session -- only a message
   * loaded this way can already have a run in progress with history to
   * replay. See the same field on useAgentChatRun.
   */
  _runsAreHistorical?: boolean;
  /** Workflow this turn looked up, when the turn is about exactly one. */
  _workflowId?: string;
  /** The question this turn ended on, when it asked the user to choose. */
  _decision?: AgentChatDecision;
  /** The option this turn picked, when it answers such a question. */
  _decisionResponse?: AgentChatDecisionResponse;
  /** Turn start (epoch ms) for the live elapsed-time indicator. */
  _startedAt?: number;
  /** A paused node's review in progress, shown until its reply lands. */
  _thinking?: string;
  /**
   * The turn's thinking, intermediate messages and steps, in stream order.
   * Only live turns have one; `content` holds the reply in progress.
   */
  _timeline?: TurnSegment[];
  /**
   * Canvas mutations proposed during this turn, flushed at turn end.
   * Rendered as a confirm card; applied when the user clicks "Apply".
   */
  _pendingMutations?: WorkflowMutationEntry[];
  /**
   * Workflow that was on screen when these mutations were proposed. In a
   * surface host the mounted canvas can switch to a different workflow
   * before the card is applied -- comparing against this (not whatever the
   * canvas shows now) is what keeps a stale card from landing on the wrong
   * graph.
   */
  _pendingMutationsWorkflowId?: string | null;
  /** True once the user has applied the pending mutations. */
  _mutationsApplied?: boolean;
  /**
   * Set on marker rows only: what the conversation switched to here. Such a
   * row is not a turn -- it renders as a marker, never as a bubble.
   */
  _contextSwitch?: ContextSwitch;
  /**
   * A row a run wrote through the operator -- a node's question, how it
   * ended, later a draft or the run's result. Badged in the transcript and
   * what tells the composer a question is waiting.
   */
  _runEvent?: RelayedRunEvent;
}

export type ChatAttachmentRef = Pick<UploadedAgentFile, 'fileId' | 'filename'>;

export type { PausedNode } from './paused-node';

/** Artifacts persisted in message metadata survive page reloads. */
function artifactsFromMetadata(m: AgentChatMessage): AgentChatArtifact[] {
  const raw = m.metadata?.artifacts;
  if (Array.isArray(raw)) return raw as AgentChatArtifact[];
  // A run's result row carries what it produced inside its run event.
  return runEventFromMetadata(m)?.artifacts ?? [];
}

function toUiMessages(rows: AgentChatMessage[]): AgentChatUiMessage[] {
  return rows.map((r, i) => ({
    ...r,
    _localId: `srv-${i}-${r.created_at ?? ''}`,
    _artifacts: artifactsFromMetadata(r),
    _runIds: runIdsFromMetadata(r),
    _runsAreHistorical: !!runIdsFromMetadata(r),
    _workflowId: workflowIdFromMetadata(r),
    _decision: decisionFromMetadata(r),
    _decisionResponse: decisionResponseFromMetadata(r),
    _contextSwitch: contextSwitchFromMetadata(r),
    _runEvent: runEventFromMetadata(r),
  }));
}

// A question is still open only if nothing was said after it: any later
// turn means the user already answered or moved on. A marker is not a turn.
function openDecisionIn(
  loaded: AgentChatUiMessage[],
): AgentChatDecision | null {
  const lastTurn = loaded.filter((m) => !m._contextSwitch).at(-1);
  return lastTurn?.role === 'assistant' && lastTurn._decision
    ? lastTurn._decision
    : null;
}

/**
 * Runs to re-attach on reload.
 *
 * Prefers the list. The singular fallback covers turns persisted before
 * the server wrote one, and resolves to the LAST run of that turn -- it
 * was overwritten by each `run_started` -- so a reloaded two-run turn
 * from before then shows its second card only.
 */
function runIdsFromMetadata(m: AgentChatMessage): string[] | undefined {
  const list = m.metadata?.workflow_run_ids;
  if (Array.isArray(list)) {
    const ids = list.filter(
      (id): id is string => typeof id === 'string' && !!id,
    );
    if (ids.length) return ids;
  }
  const runId = m.metadata?.workflow_run_id;
  return typeof runId === 'string' && runId ? [runId] : undefined;
}

function workflowIdFromMetadata(m: AgentChatMessage): string | undefined {
  const workflowId = m.metadata?.workflow_id;
  return typeof workflowId === 'string' && workflowId ? workflowId : undefined;
}

/** The question an assistant turn ended on, as persisted server-side. */
function decisionFromMetadata(
  m: AgentChatMessage,
): AgentChatDecision | undefined {
  return parseDecision(m.metadata?.decision);
}

function decisionResponseFromMetadata(
  m: AgentChatMessage,
): AgentChatDecisionResponse | undefined {
  const raw = m.metadata?.decision_response as
    | AgentChatDecisionResponse
    | undefined;
  return raw && typeof raw === 'object' && typeof raw.decision_id === 'string'
    ? raw
    : undefined;
}

/**
 * The context a stored transcript was last recorded as being in, or null when
 * it has no markers at all. Read off the loaded messages rather than tracked
 * separately, because the transcript is what the server dedupes against.
 */
function lastContextIdentity(messages: AgentChatUiMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const context = messages[i]._contextSwitch;
    if (context) return contextIdentity(context);
  }
  return null;
}

export interface UseAgentChatOptions {
  orgId?: string;
  agentId?: string;
  conversationId: string | null;
  onConversationChange?: (id: string) => void;
  surfaceContext?: SurfaceContext | null;
  /**
   * The workflow canvas this chat is grounded in, independent of whether
   * its `surfaceContext` has finished resolving -- `surfaceContext` goes
   * null while the canvas is still loading (or transiently, e.g. a
   * refetch), and freezing a turn's canvas identity off that would
   * otherwise treat "still loading" the same as "no canvas at all". Null
   * when the chat is grounded in no canvas; `'new'` for an unsaved one.
   * This is the identity mutations and runs are captured against, and a
   * host may keep it after the user dismisses the pane itself: whether the
   * canvas is on screen is the provider's `canvasVisible`, not this.
   */
  openWorkflowId?: string | null;
  /**
   * True while the host is still finding out what the surface is -- an
   * artifact whose title has not loaded, say. Holds the context rather than
   * recording a switch to something half-known.
   */
  surfaceResolving?: boolean;
  modelOverride?: string | null;
  customInstructions?: string;
  /** Org skill every outgoing message carries a load_skill directive for. */
  armedSkill?: ArmedSkill | null;
  onArtifact?: (artifact: AgentChatArtifact) => void;
  /**
   * Every artifact frame the stored transcript carries, in order, when a
   * conversation loads. What the conversation produced -- not a signal to
   * show anything, unlike onArtifact.
   */
  onArtifactHistory?: (artifacts: AgentChatArtifact[]) => void;
  onRunStarted?: (runId: string) => void;
  onTurnComplete?: () => void;
  /**
   * Called when the user clicks "Apply" on a canvas mutation confirm card.
   * Return `false` to signal the apply was blocked or failed outright (e.g.
   * the editing lock engaged since the card was shown) — the card stays
   * un-applied so the user can retry.
   */
  onApplyMutations?: (
    mutations: WorkflowMutationEntry[],
    targetWorkflowId?: string | null,
    applyKey?: string,
  ) => boolean | void;
  /** Called when the agent requests a canvas run (canvas_run_workflow frame). */
  onRunWorkflow?: (targetWorkflowId?: string | null) => Promise<string | null>;
  /**
   * True while the caller is still resolving which conversation this surface
   * should resume (see useSurfaceConversation). Forces writeAccess to
   * 'unknown' regardless of conversationId -- a null id is otherwise treated
   * as "fresh thread, composer allowed", which would let a message through
   * before the real linked conversation is known.
   */
  conversationLinkResolving?: boolean;
}

/** Resolves an instance id against the live canvas store's node structs. */
// Nodes added earlier in the same proposal aren't on the canvas yet, so the
// batch itself is the only place their names exist.
function labelForCanvasMutation(
  mutation: AgentChatCanvasMutation,
  earlierInBatch: WorkflowMutationEntry[],
): string {
  return buildMutationLabel(mutation, (instanceId) => {
    const node = useNodeStore.getState().nodes.find((n) => n.id === instanceId);
    const canvasName = getStructDisplayLabel(node?.data?.struct);
    if (canvasName) return canvasName;
    const added = earlierInBatch.find(
      ({ eventData }) =>
        eventData.event_type === 'add_node' &&
        eventData.instance_id === instanceId,
    );
    return added ? addedNodeDisplayName(added.eventData) : instanceId;
  });
}

export function useAgentChat({
  orgId,
  agentId,
  conversationId,
  onConversationChange,
  surfaceContext,
  openWorkflowId = null,
  surfaceResolving = false,
  modelOverride,
  customInstructions,
  armedSkill,
  onArtifact,
  onArtifactHistory,
  onRunStarted,
  onTurnComplete,
  onApplyMutations,
  onRunWorkflow,
  conversationLinkResolving = false,
}: UseAgentChatOptions) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [messages, setMessages] = useState<AgentChatUiMessage[]>([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  // Seeded from the prop: a mount that already knows its conversation is
  // loading before the effect below runs, otherwise the first paint reads as
  // an empty chat and flashes the onboarding empty state.
  const [isLoadingMessages, setIsLoadingMessages] = useState(!!conversationId);
  const [isSending, setIsSending] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The conversation id this hook's own stream minted mid-turn (the start
  // frame of a brand-new chat's first send), as opposed to one the caller
  // navigated to. The prompt queue keys its draft handoff off this.
  const [adoptedConversationId, setAdoptedConversationId] = useState<
    string | null
  >(null);
  // null until the server says otherwise: fresh conversations and the
  // owner's own chats are writable; only an explicit 'viewer' (a chat
  // shared with the caller) locks the composer.
  const [conversationRole, setConversationRole] =
    useState<ConversationRole | null>(null);
  // Server-side title: a share viewer's own conversation list won't contain
  // the shared conversation, so the header can't derive a label from it.
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null,
  );
  // The question the composer is currently showing instead of a text box.
  const [pendingDecision, setPendingDecision] =
    useState<AgentChatDecision | null>(null);
  // Set when the user answers with "Something else": the panel closes, but
  // the question stays armed so what they type next still reaches the agent
  // as the answer to it rather than as an unrelated message.
  const freeTextDecisionRef = useRef<string | null>(null);

  const writeAccess = conversationLinkResolving
    ? 'unknown'
    : conversationWriteAccess({
        conversationId,
        role: conversationRole,
        isLoading: isLoadingMessages,
      });
  // Read inside sendMessage, which is memoized on identities rather than
  // this value; a ref keeps the guard honest without re-creating the
  // callback on every load transition.
  const writeAccessRef = useRef(writeAccess);
  writeAccessRef.current = writeAccess;

  // Live-turn controls. Pause freezes the visible bubble by queueing
  // message updates (the SSE connection stays open so no data is lost);
  // stop aborts the fetch and finalizes the bubble with whatever arrived.
  const abortRef = useRef<AbortController | null>(null);
  const activeAssistantIdRef = useRef<string | null>(null);
  const pausedRef = useRef(false);
  // Synchronous double-send gate: React state lags a render, so a second
  // Enter/click before re-render would still see isSending=false and start
  // a duplicate stream for the same turn.
  const isSendingRef = useRef(false);
  // Canvas mutation buffer: events are collected during a turn and flushed
  // onto the assistant message on done so the confirm card can render once.
  const pendingMutationsRef = useRef<WorkflowMutationEntry[]>([]);
  // Workflow visible when this turn's mutations started buffering. `null`
  // once resolved (including "no canvas"), `undefined` only before the
  // first mutation of the turn.
  const pendingMutationsWorkflowIdRef = useRef<string | null | undefined>(
    undefined,
  );
  // One canvas run per turn; reset when the next turn starts.
  const runDispatchedThisTurnRef = useRef(false);
  // Keep stable refs so closures don't capture stale callbacks.
  const onApplyMutationsRef = useRef(onApplyMutations);
  const onRunWorkflowRef = useRef(onRunWorkflow);
  // A surface fills in after the panel mounts — the canvas has to load before
  // it can describe itself — so a send bound to the mount-time value would
  // ground the turn against an empty screen.
  const surfaceContextRef = useRef(surfaceContext);
  surfaceContextRef.current = surfaceContext;
  const openWorkflowIdRef = useRef(openWorkflowId);
  openWorkflowIdRef.current = openWorkflowId;
  const pendingUpdatesRef = useRef<
    Array<(m: AgentChatUiMessage) => AgentChatUiMessage>
  >([]);
  useEffect(() => {
    onApplyMutationsRef.current = onApplyMutations;
  }, [onApplyMutations]);
  useEffect(() => {
    onRunWorkflowRef.current = onRunWorkflow;
  }, [onRunWorkflow]);

  // A ref (not React state) so the guard is synchronous: setState's updater
  // isn't guaranteed to run before this function returns, and React may
  // invoke it more than once for a single update, so it can't safely be
  // where the once-only side effect (onApplyMutations) lives.
  const appliedMutationIdsRef = useRef<Set<string>>(new Set());

  const applyMutations = useCallback(
    (
      messageLocalId: string,
      mutations: WorkflowMutationEntry[],
      targetWorkflowId?: string | null,
    ) => {
      if (
        !mutations.length ||
        // null means this proposal was captured with no canvas open at all
        // (see onMutation) -- there is no workflow it could legitimately
        // apply to, so refuse outright rather than let it land on whatever
        // canvas happens to be mounted when the user clicks Apply.
        targetWorkflowId === null ||
        (appliedMutationIdsRef.current.has(messageLocalId) &&
          useChatApplyStore.getState().records[messageLocalId]?.status !==
            'undone')
      ) {
        return;
      }
      // Added before the call (not after) so a second synchronous call --
      // e.g. a double click before the first click's re-render disables the
      // button -- still sees it and bails above.
      appliedMutationIdsRef.current.add(messageLocalId);
      const result = onApplyMutationsRef.current?.(
        mutations,
        targetWorkflowId,
        messageLocalId,
      );
      if (result === false) {
        // Blocked or failed outright (e.g. the editing lock engaged since
        // the card was shown) -- let the user retry. Cleared on the message
        // too: a re-apply after an undo starts from an applied message.
        appliedMutationIdsRef.current.delete(messageLocalId);
        setMessages((prev) =>
          prev.map((m) =>
            m._localId === messageLocalId && m._mutationsApplied
              ? { ...m, _mutationsApplied: false }
              : m,
          ),
        );
        return;
      }
      setMessages((prev) =>
        prev.map((m) =>
          m._localId === messageLocalId ? { ...m, _mutationsApplied: true } : m,
        ),
      );
    },
    [],
  );

  // Identifies the current draft canvas across a save: workflow_id is
  // omitted for an unsaved canvas, and a flat 'new' can't tell today's
  // draft apart from one the user replaced without ever leaving the 'new'
  // route (e.g. picking a different starter template) -- createdAt is
  // reset fresh by both createBlankWorkflow and loadTemplateWorkflow, and
  // is round-tripped unchanged through a save (see saveWorkflow in
  // workflow-store.tsx), so it uniquely names this specific draft.
  const currentWorkflowCreatedAt = useWorkflowStore(
    (s) => s.currentWorkflow?.createdAt,
  );
  const currentWorkflowCreatedAtRef = useRef(currentWorkflowCreatedAt);
  currentWorkflowCreatedAtRef.current = currentWorkflowCreatedAt;
  // saveWorkflow sets this to the real uuid immediately on save, well before
  // the post-create navigation lands and openWorkflowId (the URL param)
  // catches up -- see isNewWorkflow in workflow-editor-page.tsx.
  const currentWorkflowIdFromStore = useWorkflowStore(
    (s) => s.currentWorkflow?.id ?? null,
  );
  const currentWorkflowIdFromStoreRef = useRef(currentWorkflowIdFromStore);
  currentWorkflowIdFromStoreRef.current = currentWorkflowIdFromStore;

  // Names a context switch is recorded under. The surface context the agent
  // is grounded with carries ids, not names, so the name has to come from
  // the store the canvas already keeps.
  const currentWorkflowName = useWorkflowStore(
    (s) => s.currentWorkflow?.name ?? null,
  );

  // `null` means "not known yet", which is not the same as "nothing open".
  // A mounted canvas reports no surface context while it loads -- and a run
  // reloads the workflow -- so reading that blink as an empty context would
  // bracket every run with a spurious "back to general chat" and a re-entry.
  // openWorkflowId is the signal that survives the blink.
  const activeContext: ContextSwitch | null =
    surfaceResolving || (!surfaceContext && openWorkflowId != null)
      ? null
      : contextFromSurface(surfaceContext, {
          workflowName: currentWorkflowName,
          workflowId: currentWorkflowIdFromStore,
        });
  // Fired from inside the conversation load, which must not re-run when the
  // host happens to hand over a new callback identity.
  const onArtifactHistoryRef = useRef(onArtifactHistory);
  onArtifactHistoryRef.current = onArtifactHistory;

  const activeContextRef = useRef(activeContext);
  activeContextRef.current = activeContext;
  const lastRecordedContextRef = useRef<string | null>(null);
  // A marker shown for a context the transcript does not record until the
  // user says something in it (see `recordsOnSend`). A turn sent in it lets
  // the chat endpoint write the row; a later switch replaces or drops it.
  const pendingMarkerRef = useRef<{ localId: string; identity: string } | null>(
    null,
  );
  // Which conversation `lastRecordedContextRef` describes. The recorder
  // below compares against it, so until this names the current conversation
  // there is nothing valid to compare with.
  const [loadedConversationId, setLoadedConversationId] = useState<
    string | null
  >(null);

  // A canvas that was 'new' when a save completes gets a real store id
  // before openWorkflowId (the route/prop) has caught up -- but that's
  // observably identical to a genuine switch to a *different*, already-saved
  // workflow whose 'new' canvas hasn't reset the (global) store yet: both
  // show up as openWorkflowId === 'new' with a real, unrelated id still
  // sitting in the store. Track whether this hook has actually observed the
  // store settle on 'new' for the CURRENT 'new' request before trusting a
  // later real id as that same canvas's save, mirroring the identical
  // problem (and fix) in useWorkflowCanvasSurfaceContext.
  const prevOpenWorkflowIdRef = useRef<string | null | undefined>(undefined);
  const hasConfirmedNewCanvasRef = useRef(false);
  if (prevOpenWorkflowIdRef.current !== openWorkflowId) {
    hasConfirmedNewCanvasRef.current = false;
    prevOpenWorkflowIdRef.current = openWorkflowId;
  }
  if (openWorkflowId === 'new' && currentWorkflowIdFromStore === 'new') {
    hasConfirmedNewCanvasRef.current = true;
  }

  // A proposal made on an unsaved canvas freezes its target as this draft
  // key (see onMutation below). Once the same draft is saved, its surface
  // context resolves workflow_id to the real id -- reconcile any proposal
  // still waiting for Apply and tagged with that exact draft key onto the
  // id it just earned, or the card would stay permanently disabled for a
  // workflow that never actually changed. A proposal from a since-replaced
  // draft carries a different key and is correctly left alone.
  useEffect(() => {
    if (surfaceContext?.surface !== 'workflow_canvas') return;
    const realId = surfaceContext.workflow_id;
    if (realId === undefined) return;
    const draftKey = `new:${currentWorkflowCreatedAt ?? ''}`;
    if (pendingMutationsWorkflowIdRef.current === draftKey) {
      pendingMutationsWorkflowIdRef.current = realId;
    }
    setMessages((prev) => {
      if (!prev.some((m) => m._pendingMutationsWorkflowId === draftKey)) {
        return prev;
      }
      return prev.map((m) =>
        m._pendingMutationsWorkflowId === draftKey
          ? { ...m, _pendingMutationsWorkflowId: realId }
          : m,
      );
    });
  }, [surfaceContext, currentWorkflowCreatedAt]);

  // The canvas identity to freeze a turn's proposal/run against: the real id
  // once saved, the same `new:<createdAt>` draft key while unsaved, or null
  // when no canvas pane is open at all.
  //
  // Neither available signal is reliable on its own:
  // - openWorkflowId (the route/prop) is right the instant a pane opens, but
  //   lags behind on save -- it can stay 'new' until the post-create
  //   navigation lands, well after currentWorkflow.id already has the real
  //   uuid (see isNewWorkflow in workflow-editor-page.tsx).
  // - currentWorkflow.id (the store) is right on save, but can still be
  //   showing whatever the *previous* pane had open for a beat after
  //   switching to a workflow that hasn't loaded yet.
  // Trust the store's real id when it directly matches a specific request
  // (unambiguous -- a real id names exactly one workflow), or when the
  // request is 'new' and this hook has confirmed it's this same draft's
  // save rather than a not-yet-reset leftover from a prior workflow.
  const resolveCanvasIdentity = useCallback((): string | null => {
    const requested = openWorkflowIdRef.current;
    if (requested == null) return null;
    const loaded = currentWorkflowIdFromStoreRef.current;
    if (
      loaded &&
      loaded !== 'new' &&
      (requested === loaded ||
        (requested === 'new' && hasConfirmedNewCanvasRef.current))
    ) {
      return loaded;
    }
    return requested === 'new'
      ? `new:${currentWorkflowCreatedAtRef.current ?? ''}`
      : requested;
  }, []);

  const applyToAssistant = useCallback(
    (
      assistantId: string,
      fns: Array<(m: AgentChatUiMessage) => AgentChatUiMessage>,
    ) => {
      if (!fns.length) return;
      setMessages((prev) =>
        prev.map((m) =>
          m._localId === assistantId ? fns.reduce((acc, fn) => fn(acc), m) : m,
        ),
      );
    },
    [],
  );

  /** The per-turn refs and flags a finished/abandoned turn leaves behind. */
  const resetTurnState = useCallback(() => {
    activeAssistantIdRef.current = null;
    pendingUpdatesRef.current = [];
    pausedRef.current = false;
    setIsPaused(false);
    isSendingRef.current = false;
    setIsSending(false);
  }, []);

  const stopTurn = useCallback(() => {
    const controller = abortRef.current;
    if (!controller) return;
    abortRef.current = null;
    controller.abort();
    const assistantId = activeAssistantIdRef.current;
    const pending = pendingUpdatesRef.current;
    resetTurnState();
    if (assistantId) {
      const stoppedAt = Date.now();
      applyToAssistant(assistantId, [
        ...pending,
        (m) => ({
          ...m,
          _pending: false,
          _streaming: false,
          _timeline: m._timeline
            ? closeThinking(m._timeline, stoppedAt)
            : undefined,
          // Prose sealed into the timeline is still on screen, inline --
          // a turn that said something is not a stalled one.
          ...(m.content || hasProse(m._timeline)
            ? {}
            : {
                content:
                  '(stopped — the agent may still finish the work ' +
                  'server-side; resend to pick it back up)',
                _stalled: true,
              }),
        }),
      ]);
    }
  }, [applyToAssistant, resetTurnState]);

  const pauseTurn = useCallback(() => {
    if (!abortRef.current) return;
    pausedRef.current = true;
    setIsPaused(true);
  }, []);

  /**
   * Abandon the in-flight turn on the way out of a conversation.
   *
   * Unlike stopTurn this says nothing about the bubble it leaves behind:
   * that transcript is about to be replaced, and the turn may well finish
   * server-side. What matters is the send gate, which otherwise stays shut
   * until the abandoned stream ends on its own -- leaving the composer
   * disabled in the conversation the user just opened.
   */
  const releaseActiveStream = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    resetTurnState();
  }, [resetTurnState]);

  const resumeTurn = useCallback(() => {
    pausedRef.current = false;
    setIsPaused(false);
    const assistantId = activeAssistantIdRef.current;
    const pending = pendingUpdatesRef.current;
    pendingUpdatesRef.current = [];
    if (assistantId) applyToAssistant(assistantId, pending);
  }, [applyToAssistant]);

  // Set when a conversation id is adopted mid-stream so the
  // load-messages effect skips one server reload (see below).
  const skipNextConversationLoadRef = useRef(false);
  const conversationIdRef = useRef(conversationId);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // A panel that unmounts mid-turn should not leave its fetch running.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (!orgId || !agentId || !conversationId) {
      releaseActiveStream();
      setMessages([]);
      setConversationRole(null);
      setConversationTitle(null);
      freeTextDecisionRef.current = null;
      setPendingDecision(null);
      lastRecordedContextRef.current = null;
      pendingMarkerRef.current = null;
      setLoadedConversationId(null);
      setIsLoadingMessages(false);
      return;
    }
    // When the conversation id was just adopted from the stream's start
    // frame, the local message list (user turn + live-streaming assistant
    // bubble) is already the source of truth -- reloading from the server
    // here would wipe the streaming bubble mid-turn and orphan every
    // subsequent SSE update (the "reply streams invisibly" bug).
    if (skipNextConversationLoadRef.current) {
      skipNextConversationLoadRef.current = false;
      // The local list is this conversation's transcript, and the send that
      // minted the id already recorded where it was.
      setLoadedConversationId(conversationId);
      return;
    }
    // Past the skip check, so this is a genuine move to another
    // conversation rather than the current one adopting its id. Clear the
    // previous conversation's question immediately rather than waiting on
    // the load below -- otherwise it stays armed and answerable against
    // the new conversation for as long as the load takes, or forever if
    // it fails.
    releaseActiveStream();
    freeTextDecisionRef.current = null;
    setPendingDecision(null);
    // Forget where the previous conversation was, and whose it was, before
    // this one loads: the silent-switch recorder below is gated on both, and
    // left standing they would have it compare the new chat's surface
    // against the old transcript -- with the old chat's write access.
    setConversationRole(null);
    lastRecordedContextRef.current = null;
    pendingMarkerRef.current = null;
    setLoadedConversationId(null);
    let cancelled = false;
    (async () => {
      setIsLoadingMessages(true);
      setError(null);
      try {
        const detail = await getAgentConversationDetail(
          orgId,
          agentId,
          conversationId,
        );
        if (cancelled) return;
        const rows = detail?.messages ?? [];
        setConversationRole(detail?.user_role ?? null);
        setConversationTitle(detail?.title ?? null);
        const loaded = toUiMessages(rows);
        setMessages(loaded);
        onArtifactHistoryRef.current?.(
          loaded.flatMap((m) => m._artifacts ?? []),
        );
        // Pick up where the stored transcript leaves off, so reopening a
        // conversation on the context it was left in announces nothing.
        lastRecordedContextRef.current = lastContextIdentity(loaded);
        setLoadedConversationId(conversationId);
        freeTextDecisionRef.current = null;
        setPendingDecision(openDecisionIn(loaded));
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : 'Failed to load conversation',
          );
        }
      } finally {
        if (!cancelled) setIsLoadingMessages(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, agentId, conversationId, releaseActiveStream]);

  // Rows a run writes into this conversation between turns arrive through
  // the user event stream with no content of their own; the transcript is
  // re-read to pick them up. A re-read only swaps the rows: nothing else
  // about the conversation changes, so the composer, its draft and the
  // caller's role stay as they are. Mid-turn the re-read waits for the turn
  // to end, since replacing the list would drop the bubble still streaming.
  const refreshTranscript = useCallback(async () => {
    const current = conversationIdRef.current;
    if (!orgId || !agentId || !current) return;
    try {
      const detail = await getAgentConversationDetail(orgId, agentId, current);
      if (conversationIdRef.current !== current) return;
      const loaded = toUiMessages(detail?.messages ?? []);
      // What only this client knows about a row -- a proposed change and
      // whether it was applied, the turn's activity -- is not on the server
      // and must survive the swap. Rows keep their ids across a re-read
      // (an index plus the row's own timestamp), so it carries over by id.
      setMessages((current) => {
        const known = new Map(current.map((m) => [m._localId, m]));
        return loaded.map((row) => {
          const before = known.get(row._localId);
          if (!before) return row;
          return {
            ...row,
            _timeline: before._timeline,
            _startedAt: before._startedAt,
            _thinking: before._thinking,
            _pendingMutations: before._pendingMutations,
            _pendingMutationsWorkflowId: before._pendingMutationsWorkflowId,
            _mutationsApplied: before._mutationsApplied,
          };
        });
      });
      lastRecordedContextRef.current = lastContextIdentity(loaded);
      if (detail?.user_role) setConversationRole(detail.user_role);
      // A question the user already dismissed, or chose to answer in prose,
      // stays that way: the decision state is this client's, not the row's.
    } catch (e) {
      // The rows already on screen stand; the next event or load tries again.
      Sentry.captureException(e, {
        extra: { context: 'agent-chat-refresh-transcript', current },
      });
    }
  }, [orgId, agentId]);
  const reloadWantedRef = useRef(false);
  const reloadMessages = useCallback(() => {
    if (!conversationIdRef.current) return;
    if (isSendingRef.current) {
      reloadWantedRef.current = true;
      return;
    }
    void refreshTranscript();
  }, [refreshTranscript]);
  useEffect(() => {
    if (isSending || !reloadWantedRef.current) return;
    reloadWantedRef.current = false;
    void refreshTranscript();
  }, [isSending, refreshTranscript]);

  // A conversation id given to this chat from outside, before any turn: a
  // canvas Run beside a new chat mints one so the run's first question has
  // somewhere to land. The chat adopts it the way it adopts the id a turn's
  // start frame carries -- as its own, with nothing to load yet.
  const adoptMintedConversation = useCallback(
    (id: string) => {
      if (!id || id === conversationIdRef.current) return;
      skipNextConversationLoadRef.current = true;
      conversationIdRef.current = id;
      setAdoptedConversationId(id);
      onConversationChange?.(id);
    },
    [onConversationChange],
  );

  // Switches that happen between turns: shown at once, but written by the
  // chat endpoint with the next message (see `recordsOnSend`). Only a review
  // pause is written here, when it happens.
  //
  // Keyed on the identity rather than the context object: the surface context
  // is rebuilt on every canvas edit, and editing a workflow is not leaving it.
  const switchIdentity = activeContext ? contextIdentity(activeContext) : null;
  // The canvas a selected node sits on. A save that lands while a node is
  // selected changes this and nothing else the effect keys on.
  const selectedNodeCanvasId =
    activeContext?.kind === 'workflow_node'
      ? (activeContext.workflow_id ?? null)
      : null;
  useEffect(() => {
    if (!orgId || !agentId || !conversationId) return;
    // The transcript this hook knows is another conversation's (or none):
    // wait for this one's, or the comparison below is against the wrong
    // chat -- with the wrong chat's write access.
    if (loadedConversationId !== conversationId) return;
    // Context still resolving -- hold, rather than announce a change to
    // something we do not yet know.
    if (switchIdentity === null) return;
    // A viewer is reading someone else's transcript; the endpoint is
    // owner-only and annotating their chat is not the viewer's to do.
    if (writeAccess !== 'allowed') return;
    // The reply in flight is saved only when its stream ends; a switch
    // recorded now would sit between the message and its reply. Re-runs
    // when the turn finishes.
    if (isSending) return;
    const pending = pendingMarkerRef.current;
    // A shown-but-unrecorded marker the user has moved away from, having said
    // nothing under it, described a stretch that never happened.
    const dropPending = () => {
      if (!pending) return;
      pendingMarkerRef.current = null;
      setMessages((prev) => prev.filter((m) => m._localId !== pending.localId));
    };
    if (switchIdentity === lastRecordedContextRef.current) {
      dropPending();
      return;
    }
    const savedWhileUnsaved =
      lastRecordedContextRef.current === UNSAVED_CANVAS_IDENTITY &&
      openWorkflowIdRef.current === 'new';
    if (pending?.identity === switchIdentity) {
      // Still on the node, but the draft canvas under it was saved: adopt the
      // canvas's new identity now, so deselecting later reads as returning
      // to it rather than as a switch to announce.
      if (savedWhileUnsaved && selectedNodeCanvasId) {
        lastRecordedContextRef.current = `workflow_canvas:${selectedNodeCanvasId}`;
      }
      return;
    }
    // The canvas the user is on was unsaved when it was last recorded and has
    // since been saved: its id arrived, but they never left it. Adopt the new
    // identity rather than announce a switch to the workflow already open.
    //
    // Only while the pane is still asking for the same 'new' canvas: that is
    // what tells a save apart from opening another workflow from a draft,
    // which changes what is asked for and is a switch to announce.
    if (savedWhileUnsaved && switchIdentity.startsWith('workflow_canvas:')) {
      lastRecordedContextRef.current = switchIdentity;
      dropPending();
      return;
    }
    // Nothing recorded yet and nothing in focus: there is no switch to
    // describe, only the absence of one.
    if (lastRecordedContextRef.current === null && switchIdentity === 'none') {
      dropPending();
      return;
    }

    const context = activeContextRef.current;
    if (!context) return;
    // A relayed question or draft waiting here holds the conversation in
    // the context it opened; the next reply answers it (see sendMessage),
    // so a marker for where the user happens to be would never be spoken in.
    if (
      recordsOnSend(context) &&
      (pendingClarificationIn(messagesRef.current) ||
        pendingReviewIn(messagesRef.current))
    ) {
      dropPending();
      return;
    }
    const localId = `local-context-${Date.now()}`;
    const marker = {
      _localId: localId,
      role: 'system' as const,
      content: '',
      _contextSwitch: context,
    };
    // One pending marker at a time: clicking on to another node replaces the
    // one shown for the last, the way the server rewrites an unspoken row.
    const withoutPending = (prev: AgentChatUiMessage[]) =>
      pending ? prev.filter((m) => m._localId !== pending.localId) : prev;
    if (recordsOnSend(context)) {
      pendingMarkerRef.current = { localId, identity: switchIdentity };
      setMessages((prev) => [...withoutPending(prev), marker]);
      return;
    }
    pendingMarkerRef.current = null;
    // Claimed -- and shown -- before the request settles. A turn sent while
    // the write is in flight reads the claim and suppresses its own marker,
    // so the marker has to already be in the transcript by then, or the live
    // view ends up with none where the server has one.
    const previous = lastRecordedContextRef.current;
    lastRecordedContextRef.current = switchIdentity;
    setMessages((prev) => [...withoutPending(prev), marker]);

    let cancelled = false;
    (async () => {
      try {
        // A deduped write (a second tab got there first, or a transcript
        // whose load failed left this hook not knowing where it was) still
        // leaves the conversation in this context, so the marker stands.
        await recordContextSwitch(orgId, agentId, conversationId, context);
      } catch (e) {
        if (cancelled) return;
        // Nothing was written: take the marker back and put the claim back,
        // so the next switch still has something to differ from. A missing
        // marker is a gap in the record, never a reason to interrupt the chat.
        setMessages((prev) => prev.filter((m) => m._localId !== localId));
        lastRecordedContextRef.current = previous;
        Sentry.captureException(e, {
          extra: { conversationId, context, agentId, orgId },
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    orgId,
    agentId,
    conversationId,
    loadedConversationId,
    switchIdentity,
    selectedNodeCanvasId,
    writeAccess,
    isSending,
  ]);

  const sendMessage = useCallback(
    async (
      text: string,
      attachments: ChatAttachmentRef[] = [],
      decisionResponse?: AgentChatDecisionResponse,
    ) => {
      const trimmed = text.trim();
      if (
        (!trimmed && attachments.length === 0) ||
        !orgId ||
        !agentId ||
        // Not just 'denied': while the role is still unknown this could be
        // a view-only share, and the server would refuse the turn.
        writeAccessRef.current !== 'allowed' ||
        isSendingRef.current
      ) {
        // false = refused outright, nothing happened. The prompt queue
        // relies on this to put a dispatched prompt back instead of
        // losing it to the no-op.
        return false;
      }

      // Gated on both a canvas being associated with this session
      // (openWorkflowId) and an apply handler being wired. Neither alone is
      // enough: chat-home and the artifact viewer wire a cross-pane apply
      // dispatcher unconditionally, even with no canvas pane layered
      // anywhere, so onApplyMutations alone can't tell "on the canvas"
      // apart from "nowhere near one" -- and surfaceContext can't be the
      // gate either, since it's null both then and while a real canvas is
      // still loading or an unsaved draft with nothing on it yet.
      // openWorkflowId is the one signal that's non-null (even 'new')
      // exactly when, and only when, a canvas is genuinely associated with
      // this session, loaded or not.
      if (
        attachments.length === 0 &&
        !decisionResponse &&
        openWorkflowIdRef.current != null &&
        onApplyMutationsRef.current &&
        isApplyChangesIntent(trimmed)
      ) {
        // Mirrors the card's own eligibility check (message-bubble.tsx),
        // including its "unknown visible workflow" default: when the canvas
        // identity hasn't resolved yet, a captured (non-null) target isn't
        // treated as stale. Once it has resolved, a proposal captured for a
        // different workflow -- stale after a canvas switch -- is treated
        // the same as having nothing pending, instead of landing on the
        // wrong canvas.
        const currentWorkflowKey =
          surfaceContextRef.current?.surface === 'workflow_canvas'
            ? (surfaceContextRef.current.workflow_id ??
              `new:${currentWorkflowCreatedAtRef.current ?? ''}`)
            : null;
        const proposal = [...messagesRef.current].reverse().find((message) => {
          if (
            message._mutationsApplied ||
            (message._pendingMutations?.length ?? 0) === 0
          ) {
            return false;
          }
          const targetId = message._pendingMutationsWorkflowId;
          const isStale =
            targetId === null ||
            (!!targetId &&
              !!currentWorkflowKey &&
              targetId !== currentWorkflowKey);
          return !isStale;
        });
        if (proposal) {
          applyMutations(
            proposal._localId,
            proposal._pendingMutations!,
            proposal._pendingMutationsWorkflowId,
          );
          return true;
        }
        notification.info(
          'There are no pending changes to apply. Ask the agent to propose an edit first.',
        );
        // true: the message was consumed (the user got their answer), even
        // though no turn ran -- a retry would only repeat the notice.
        return true;
      }

      // Typing after "Something else" still answers the open question.
      const armedDecisionId = freeTextDecisionRef.current;
      const answer: AgentChatDecisionResponse | undefined =
        decisionResponse ??
        (armedDecisionId && trimmed
          ? { decision_id: armedDecisionId, free_text: trimmed }
          : undefined);
      // Whatever the user says next supersedes the question, answered or not.
      freeTextDecisionRef.current = null;
      setPendingDecision(null);

      // The wrapped form is what the server persists, so the local bubble
      // stores it too and MessageBubble strips it for display -- live sends
      // and reloaded conversations then render identically. Order matters:
      // custom instructions are the outermost block (their strip regex is
      // anchored to the start of the message).
      const messageToSend = wrapWithCustomInstructions(
        wrapWithSkillDirective(
          formatAgentMessageWithAttachments(trimmed, attachments),
          armedSkill,
        ),
        customInstructions ?? '',
      );

      isSendingRef.current = true;
      setIsSending(true);
      setError(null);

      const userLocalId = `local-user-${Date.now()}`;
      const assistantLocalId = `local-assistant-${Date.now()}`;
      // A turn asked in a context the conversation has not been recorded in
      // gets its marker from the chat endpoint, ahead of the turn itself.
      // Mirror that locally so the live transcript and the reloaded one read
      // the same way -- this is the path that covers a brand-new
      // conversation, where there was no conversation to annotate until now.
      // Null while a mounted canvas is still resolving. A turn sent in that
      // window carries no context rather than a wrong one, and the server
      // leaves the conversation on whatever it last recorded.
      // A reply while a relayed question or draft waits here answers it,
      // so it stays in the context that ask opened rather than switching
      // back to wherever the user happens to be.
      const answersRelayedAsk =
        !!pendingClarificationIn(messagesRef.current) ||
        !!pendingReviewIn(messagesRef.current);
      const turnContext = answersRelayedAsk ? null : activeContextRef.current;
      const turnIdentity = turnContext ? contextIdentity(turnContext) : null;
      const announcesContext =
        turnContext !== null &&
        turnIdentity !== lastRecordedContextRef.current &&
        !(
          lastRecordedContextRef.current === null && turnContext.kind === 'none'
        );
      if (announcesContext && turnIdentity) {
        lastRecordedContextRef.current = turnIdentity;
      }
      // A marker already shown for this turn's context becomes the recorded
      // one -- the endpoint writes the row ahead of the turn -- so it stays
      // and is not shown twice. One shown for some other context described
      // a stretch nothing was said in.
      const pendingMarker = pendingMarkerRef.current;
      pendingMarkerRef.current = null;
      const pendingIsThisContext = pendingMarker?.identity === turnIdentity;
      setMessages((prev) => [
        ...(pendingMarker && !pendingIsThisContext
          ? prev.filter((m) => m._localId !== pendingMarker.localId)
          : prev),
        ...(announcesContext && turnContext && !pendingIsThisContext
          ? [
              {
                _localId: `local-context-${Date.now()}`,
                role: 'system' as const,
                content: '',
                _contextSwitch: turnContext,
              },
            ]
          : []),
        {
          _localId: userLocalId,
          role: 'user',
          content: messageToSend,
          _decisionResponse: answer,
        },
        {
          _localId: assistantLocalId,
          role: 'assistant',
          content: '',
          _pending: true,
          _streaming: true,
          _timeline: [],
          _artifacts: [],
          _startedAt: Date.now(),
        },
      ]);

      const controller = new AbortController();
      abortRef.current = controller;
      activeAssistantIdRef.current = assistantLocalId;
      pausedRef.current = false;
      pendingUpdatesRef.current = [];
      pendingMutationsRef.current = [];
      // Frozen here, at send time, rather than lazily on the turn's first
      // mutation frame -- mutations stream back well after the turn is sent,
      // and a canvas switch in that window would otherwise tag this turn's
      // proposal with the workflow the user switched to, not the one the
      // turn was actually grounded on.
      pendingMutationsWorkflowIdRef.current = resolveCanvasIdentity();
      runDispatchedThisTurnRef.current = false;
      setIsPaused(false);

      const updateAssistant = (
        fn: (m: AgentChatUiMessage) => AgentChatUiMessage,
      ) => {
        setMessages((prev) =>
          prev.map((m) => (m._localId === assistantLocalId ? fn(m) : m)),
        );
      };
      // Streaming updates respect pause; terminal frames flush + apply.
      const applyOrQueue = (
        fn: (m: AgentChatUiMessage) => AgentChatUiMessage,
      ) => {
        if (pausedRef.current) {
          pendingUpdatesRef.current.push(fn);
          return;
        }
        updateAssistant(fn);
      };
      const flushPending = () => {
        const pending = pendingUpdatesRef.current;
        pendingUpdatesRef.current = [];
        pausedRef.current = false;
        setIsPaused(false);
        if (pending.length) {
          updateAssistant((m) => pending.reduce((acc, f) => f(acc), m));
        }
      };
      const patchAssistant = (patch: Partial<AgentChatUiMessage>) => {
        flushPending();
        updateAssistant((m) => ({ ...m, ...patch }));
      };

      // A run id reaches us from three places -- the tool_result text, the
      // run_started frame and the done frame -- and a turn may start more
      // than one run. Deduping on a per-turn set rather than on message
      // state keeps both true while paused, when updates are queued and
      // the message we would have compared against is stale.
      const seenRunIds = new Set<string>();
      const attachRun = (runId: string) => {
        if (!runId || seenRunIds.has(runId)) return;
        seenRunIds.add(runId);
        applyOrQueue((message) => ({
          ...message,
          _runIds: [...(message._runIds ?? []), runId],
        }));
        onRunStarted?.(runId);
      };

      const attachWorkflow = (workflowId: string) => {
        if (!workflowId) return;
        applyOrQueue((message) =>
          message._workflowId === workflowId
            ? message
            : { ...message, _workflowId: workflowId },
        );
      };

      const adoptConversationId = (id: string) => {
        if (!id || id === conversationIdRef.current) return;
        // Only arm the skip when the id actually changes -- that's the
        // only case where the load-messages effect will fire.
        skipNextConversationLoadRef.current = true;
        conversationIdRef.current = id;
        // Tells the prompt queue this key change is the draft becoming a
        // conversation, not the user leaving for another one.
        setAdoptedConversationId(id);
        onConversationChange?.(id);
      };

      const metadata: Record<string, unknown> = {};
      const surface = surfaceContextRef.current;
      if (surface) metadata.surface_context = surface;
      // What the turn was asked in, in the form the transcript stores. Sent
      // on every turn, not just changed ones: the server decides whether it
      // is a switch by comparing against what it has, which is the only
      // comparison that holds with a second tab open.
      metadata.chat_context = turnContext;
      if (modelOverride) metadata.model_override = modelOverride;
      if (answer) metadata.decision_response = answer;

      try {
        await streamAgentChat({
          orgId,
          agentId,
          message: messageToSend,
          conversationId: conversationId ?? undefined,
          metadata,
          signal: controller.signal,
          onStart: adoptConversationId,
          onActivity: (event) => {
            // Only a tool that actually launches a run gets a card. An
            // inspection tool's result echoes the run it looked up, and
            // attaching that would show the chat running a workflow the
            // user never asked for.
            if (
              event.type === 'tool_result' &&
              event.text &&
              isRunInitiatingTool(event.toolName)
            ) {
              const runId = extractRunIdFromText(event.text);
              if (runId) attachRun(runId);
            }
            if (isThinkingMarker(event)) {
              applyOrQueue((m) => ({ ...m, _pending: false }));
              return;
            }
            const at = Date.now();
            applyOrQueue((m) => {
              const seal = endsProse(event.type);
              return {
                ...m,
                _pending: false,
                _timeline: appendActivity(
                  seal
                    ? sealText(m._timeline ?? [], m.content)
                    : (m._timeline ?? []),
                  { ...event, at },
                  at,
                ),
                ...(seal ? { content: '' } : {}),
              };
            });
          },
          onThinking: (delta) => {
            const at = Date.now();
            applyOrQueue((m) => ({
              ...m,
              _pending: false,
              _timeline: appendThinking(
                sealText(m._timeline ?? [], m.content),
                delta,
                at,
              ),
              content: '',
            }));
          },
          onRunStarted: attachRun,
          onWorkflowDescribed: attachWorkflow,
          onDecision: (decision) => {
            applyOrQueue((m) => ({ ...m, _decision: decision }));
            setPendingDecision(decision);
          },
          onToken: (token) => {
            const at = Date.now();
            applyOrQueue((m) => ({
              ...m,
              _pending: false,
              content: m.content + token,
              _timeline: closeThinking(m._timeline ?? [], at),
            }));
          },
          onArtifact: (artifact) => {
            applyOrQueue((m) => ({
              ...m,
              _artifacts: [...(m._artifacts ?? []), artifact],
            }));
            if (artifact.edited && artifact.artifact_id) {
              queryClient.invalidateQueries({ queryKey: ['artifacts'] });
              queryClient.invalidateQueries({
                queryKey: ['artifact', orgId, artifact.artifact_id],
              });
            }
            onArtifact?.(artifact);
          },
          onMutation: (mutation) => {
            pendingMutationsRef.current = [
              ...pendingMutationsRef.current,
              {
                label: labelForCanvasMutation(
                  mutation,
                  pendingMutationsRef.current,
                ),
                eventData: mutation,
              },
            ];
            const snapshot = pendingMutationsRef.current;
            // Already frozen at send time (see sendMessage), not re-derived
            // here -- a canvas switch between send and this frame arriving
            // must not relabel a proposal already grounded on the workflow
            // the turn was actually sent against.
            const targetWorkflowId = pendingMutationsWorkflowIdRef.current;
            applyOrQueue((m) => ({
              ...m,
              _pendingMutations: snapshot,
              _pendingMutationsWorkflowId: targetWorkflowId,
            }));
          },
          // The server refuses a second run_workflow per turn, but the
          // frame is what actually starts a run, so honour only the
          // first one here too — a duplicate would be a whole extra run,
          // not a retry of the failed one.
          onRunWorkflow: () => {
            if (runDispatchedThisTurnRef.current) return;
            // onRunWorkflow is optional on the provider -- a stray frame with
            // no handler mounted would otherwise reach the `.then` below on
            // `undefined` and throw, since optional chaining only guards the
            // call itself.
            const runWorkflow = onRunWorkflowRef.current;
            if (!runWorkflow) return;
            runDispatchedThisTurnRef.current = true;
            // Reuses the turn's already-frozen canvas identity rather than
            // reading what's mounted right now: this frame can arrive well
            // after the turn was sent, and by then the user may have
            // switched to a different canvas that happens to still pass a
            // live mismatch check -- the run (and the dispatch guard it
            // feeds) must target the workflow the turn was actually sent
            // against, the same rule the mutations path already follows.
            const dispatchWorkflowId = pendingMutationsWorkflowIdRef.current;
            // The canvas owns the dispatch, so unlike a run the agent starts
            // itself this one has no id until it is under way. Attach it once
            // it comes back: the run card is what reads a finished run's
            // deliverables into the chat, and without an id it never mounts.
            void runWorkflow(dispatchWorkflowId).then((runId) => {
              if (!runId) return;
              attachRun(runId);
              if (dispatchWorkflowId) attachWorkflow(dispatchWorkflowId);
            });
          },
          onDone: ({
            fullMessage,
            conversationId: doneId,
            artifacts,
            workflowRunId,
            workflowId,
          }) => {
            adoptConversationId(doneId);
            if (workflowRunId) attachRun(workflowRunId);
            if (workflowId) attachWorkflow(workflowId);
            const flushedMutations = pendingMutationsRef.current;
            pendingMutationsRef.current = [];
            const doneAt = Date.now();
            // The reply shown is the one the server kept, which is the last
            // stretch of prose only -- earlier ones stay in the timeline.
            flushPending();
            updateAssistant((m) => ({
              ...m,
              _timeline: m._timeline
                ? finishTimeline(m._timeline, fullMessage, doneAt)
                : undefined,
            }));
            if (fullMessage) {
              patchAssistant({
                content: fullMessage,
                _pending: false,
                _streaming: false,
                _artifacts: artifacts,
                ...(flushedMutations.length > 0
                  ? { _pendingMutations: flushedMutations }
                  : {}),
              });
            } else {
              patchAssistant({
                content:
                  '(the model did not return a reply for this turn — try again, or check that ANTHROPIC_API_KEY is configured)',
                _pending: false,
                _streaming: false,
                _stalled: true,
                ...(flushedMutations.length > 0
                  ? { _pendingMutations: flushedMutations }
                  : {}),
              });
            }
            onTurnComplete?.();
          },
          onError: (msg) => {
            setError(msg);
            flushPending();
            const flushedMutations = pendingMutationsRef.current;
            pendingMutationsRef.current = [];
            // Keep the bubble either way: a partial reply stays as-is, and a
            // turn that died before any text explains itself instead of
            // vanishing (the agent may have finished the work server-side
            // even though the stream was severed).
            const errorAt = Date.now();
            updateAssistant((m) => ({
              ...m,
              _timeline: m._timeline
                ? closeThinking(m._timeline, errorAt)
                : undefined,
              content:
                m.content ||
                `(connection lost mid-turn: ${msg}. The agent may still have ` +
                  'completed the request — check the result or resend.)',
              _pending: false,
              _streaming: false,
              _stalled: true,
              ...(flushedMutations.length > 0
                ? { _pendingMutations: flushedMutations }
                : {}),
            }));
          },
        });
      } finally {
        // A newer turn (a fresh send, or releaseActiveStream on navigating
        // away) may already have taken over abortRef by the time this
        // settles -- only tear down state if this call is still the one
        // that's active, or it would clobber whatever replaced it.
        if (abortRef.current === controller) {
          abortRef.current = null;
          resetTurnState();
        }
      }
      return true;
    },
    [
      orgId,
      agentId,
      conversationId,
      queryClient,
      modelOverride,
      customInstructions,
      armedSkill,
      notification,
      onConversationChange,
      onArtifact,
      onRunStarted,
      onTurnComplete,
      resetTurnState,
      resolveCanvasIdentity,
      applyMutations,
    ],
  );

  /** Answer the open question by picking one of its options. */
  const answerDecision = useCallback(
    (optionIndex: number) => {
      const decision = pendingDecision;
      const label = decision?.options[optionIndex];
      if (!decision || !label) return;
      // The label goes out as the message text so the transcript reads
      // naturally for anything that only sees the history; the structured
      // answer rides alongside in metadata.
      void sendMessage(label, [], {
        decision_id: decision.decisionId,
        option_index: optionIndex,
        option_label: label,
      });
    },
    [pendingDecision, sendMessage],
  );

  /** "Something else": close the panel but keep the question armed. */
  const answerDecisionWithText = useCallback(() => {
    if (!pendingDecision) return;
    freeTextDecisionRef.current = pendingDecision.decisionId;
    setPendingDecision(null);
  }, [pendingDecision]);

  /** Dismiss the question outright -- nothing is sent, nothing is armed. */
  const dismissDecision = useCallback(() => {
    freeTextDecisionRef.current = null;
    setPendingDecision(null);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    freeTextDecisionRef.current = null;
    setPendingDecision(null);
  }, []);

  // The canvas identity to compare a proposal's captured target against:
  // the real workflow id once saved, or the same draft key an unsaved
  // canvas's own proposals are frozen with (see onMutation above) -- so a
  // still-unsaved canvas's own proposal reads as "on screen", not stale.
  const canvasWorkflowId =
    surfaceContext?.surface === 'workflow_canvas'
      ? (surfaceContext.workflow_id ?? `new:${currentWorkflowCreatedAt ?? ''}`)
      : null;

  return {
    messages,
    isLoadingMessages,
    isSending,
    isPaused,
    error,
    writeAccess,
    conversationTitle,
    reloadMessages,
    adoptMintedConversation,
    adoptedConversationId,
    pendingDecision,
    sendMessage,
    answerDecision,
    answerDecisionWithText,
    dismissDecision,
    stopTurn,
    pauseTurn,
    resumeTurn,
    clearMessages,
    applyMutations,
    canvasWorkflowId,
  };
}
